import React, { useState, useRef } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';
import { ListingCard, Heading, IconChevronLeft, IconChevronRight, NamedLink } from '../../../components';
import css from './SuggestedPanel.module.css';

/**
 * SuggestedPanel component that displays listing cards in a carousel
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array} props.listings - Array of listing objects to display
 * @param {number} [props.visibleCards] - Number of cards visible on desktop (default: 6)
 * @returns {JSX.Element} Suggested panel with carousel of listing cards
 */
const SuggestedPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    visibleCards = 6,
  } = props;

  // Use the listings directly (filtering is done in the duck)
  const displayListings = listings;

  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);

  // Ensure cards fit within container
  React.useEffect(() => {
    const adjustCardSizes = () => {
      if (carouselRef.current) {
        const container = carouselRef.current;
        const carouselContainer = container.parentElement; // .carouselContainer

        // Use the carousel container's actual available width
        const availableWidth = carouselContainer ? carouselContainer.clientWidth : container.clientWidth;

        if (window.innerWidth >= 1024) {
          // Desktop: 6 cards (or visibleCards prop)
          const gap = 12; // Match CSS
          const totalGapWidth = (visibleCards - 1) * gap;
          const cardWidth = (availableWidth - totalGapWidth) / visibleCards;

          // Set CSS custom property for dynamic card width
          container.style.setProperty('--dynamic-card-width', `${cardWidth}px`);
          container.style.setProperty('--dynamic-gap', `${gap}px`);
        } else if (window.innerWidth >= 768) {
          // Tablet: 2 cards visible, need to calculate proper width
          const gap = 16; // Match CSS gap for tablet
          const totalGapWidth = gap; // Only 1 gap between 2 cards
          const cardWidth = (availableWidth - totalGapWidth) / 2;

          // Set CSS custom property for tablet card width
          container.style.setProperty('--tablet-card-width', `${cardWidth}px`);
          container.style.setProperty('--dynamic-gap', `${gap}px`);
        }
      }
    };

    // Use a timeout to ensure the DOM is ready
    const timeoutId = setTimeout(adjustCardSizes, 100);

    // Throttled resize handler to avoid excessive calls
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(adjustCardSizes, 150);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [visibleCards]);

  const classes = classNames(rootClassName || css.root, className);

  // Calculate how many cards are actually visible based on screen size
  const [actualVisibleCards, setActualVisibleCards] = React.useState(() => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1024) {
        return visibleCards; // Desktop: use prop value
      } else if (window.innerWidth >= 768) {
        return 2; // Tablet: 2 cards visible
      } else {
        return 1; // Mobile: 1 card visible
      }
    }
    return visibleCards; // SSR fallback
  });

  // Update visible cards on window resize
  React.useEffect(() => {
    const updateVisibleCards = () => {
      let newVisibleCards;
      if (window.innerWidth >= 1024) {
        newVisibleCards = visibleCards; // Desktop: use prop value
      } else if (window.innerWidth >= 768) {
        newVisibleCards = 2; // Tablet: 2 cards visible
      } else {
        newVisibleCards = 1; // Mobile: 1 card visible
      }

      if (newVisibleCards !== actualVisibleCards) {
        setActualVisibleCards(newVisibleCards);
        // Reset current index if it's now out of bounds
        const newMaxIndex = Math.max(0, displayListings.length - newVisibleCards);
        if (currentIndex > newMaxIndex) {
          setCurrentIndex(newMaxIndex);
        }
      }
    };

    window.addEventListener('resize', updateVisibleCards);
    return () => window.removeEventListener('resize', updateVisibleCards);
  }, [visibleCards, actualVisibleCards, currentIndex, displayListings.length]);

  // Calculate how many cards we can scroll
  const maxIndex = Math.max(0, displayListings.length - actualVisibleCards);

  const scrollToIndex = (index) => {
    if (carouselRef.current) {
      const container = carouselRef.current;
      const cards = container.children;

      if (cards[index]) {
        // Get the actual position of the card we want to scroll to
        const targetCard = cards[index];
        let scrollPosition = targetCard.offsetLeft;

        // For tablet/mobile, we might need to adjust the scroll position
        // to ensure the card is properly visible within the container
        if (window.innerWidth < 1024) {
          const containerWidth = container.clientWidth;

          // Ensure we don't scroll past the end
          const maxScroll = container.scrollWidth - containerWidth;
          scrollPosition = Math.min(scrollPosition, maxScroll);
        }

        container.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
    setCurrentIndex(index);
  };

  const handlePrevious = () => {
    const newIndex = Math.max(0, currentIndex - 1);
    scrollToIndex(newIndex);
  };

  const handleNext = () => {
    const newIndex = Math.min(maxIndex, currentIndex + 1);
    scrollToIndex(newIndex);
  };

  // Don't render if no listings to display
  if (!displayListings || displayListings.length === 0) {
    return null;
  }

  const showArrows = displayListings.length > visibleCards;

  return (
    <div className={classes}>
      {/* Panel Header */}
      <div className={css.header}>
        {/* Left side - Title */}
        <div className={css.headerLeft}>
          <Heading as="h2" rootClassName={css.title}>
            <FormattedMessage id="SuggestedPanel.title" />
          </Heading>
        </div>

        {/* Right side - Explore link and navigation */}
        <div className={css.headerRight}>
          {/* Simple "See all" button for mobile/tablet */}
          <NamedLink
            name="SearchPage"
            to={{ search: '?sort=createdAt' }}
            className={css.seeAllButton}
          >
            <FormattedMessage id="SuggestedPanel.seeAll" />
          </NamedLink>

          {/* Full navigation for medium+ screens */}
          <div className={css.fullNavigation}>
            <NamedLink
              name="SearchPage"
              to={{ search: '?sort=createdAt' }}
              className={css.exploreLink}
            >
              <FormattedMessage id="SuggestedPanel.exploreAll" />
            </NamedLink>

            {/* Carousel Controls */}
            {showArrows && (
              <div className={css.controls}>
                <button
                  className={classNames(css.arrowButton, css.arrowPrev, {
                    [css.arrowDisabled]: currentIndex === 0
                  })}
                  onClick={handlePrevious}
                  disabled={currentIndex === 0}
                  aria-label="Previous listings"
                >
                  <IconChevronLeft className={css.arrowIcon} />
                </button>
                <button
                  className={classNames(css.arrowButton, css.arrowNext, {
                    [css.arrowDisabled]: currentIndex >= maxIndex
                  })}
                  onClick={handleNext}
                  disabled={currentIndex >= maxIndex}
                  aria-label="Next listings"
                >
                  <IconChevronRight className={css.arrowIcon} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Carousel Container */}
      <div className={css.carouselContainer}>
        <div
          ref={carouselRef}
          className={css.carousel}
          style={{
            '--visible-cards': visibleCards,
            '--total-cards': displayListings.length,
            '--card-gap': '12px' // Default gap for calculations
          }}
        >
          {displayListings.map((listing, index) => (
            <div key={listing.id?.uuid || index} className={css.cardWrapper}>
              <ListingCard
                listing={listing}
                renderSizes="(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 33vw"
                showAuthorInfo={false}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Dots Indicator (optional) */}
      {/* {showArrows && (
        <div className={css.dotsContainer}>
          {Array.from({ length: maxIndex + 1 }, (_, index) => (
            <button
              key={index}
              className={classNames(css.dot, {
                [css.dotActive]: index === currentIndex
              })}
              onClick={() => scrollToIndex(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )} */}
    </div>
  );
};

export default SuggestedPanel;
